import api from "./api.js";

const TransferService = {
  /**
   * Get players available in the transfer market
   * @param {Object} filters - Filter parameters
   * @param {string} filters.name - Player name filter
   * @param {string} filters.position - Position filter
   * @param {string} filters.nationality - Nationality filter (country code)
   * @param {number} filters.minAge - Minimum age filter
   * @param {number} filters.maxAge - Maximum age filter
   * @param {number} filters.minValue - Minimum value filter
   * @param {number} filters.maxValue - Maximum value filter
   * @param {string} filters.sortBy - Field to sort by
   * @param {string} filters.sortOrder - Sort order ('asc' or 'desc')
   * @param {number} filters.page - Page number for pagination
   * @param {number} filters.limit - Items per page
   * @returns {Promise<Object>} - Players list with pagination info
   */
  /**
   * Get players available in the transfer market with retry logic
   * @param {Object} filters - Filter parameters
   * @param {Object} options - Additional options including AbortController signal and retries
   * @returns {Promise<Object>} - Players list with pagination info
   */
  async getMarketPlayers(filters = {}, options = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.name) params.append("name", filters.name);
      if (filters.position) params.append("position", filters.position);
      if (filters.nationality) params.append("nationality", filters.nationality);
      if (filters.minAge) params.append("minAge", filters.minAge);
      if (filters.maxAge) params.append("maxAge", filters.maxAge);
      if (filters.minValue) params.append("minValue", filters.minValue);
      if (filters.maxValue) params.append("maxValue", filters.maxValue);
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
      if (filters.leagueId) params.append("leagueId", filters.leagueId);
      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.freeAgentsOnly) params.append("freeAgentsOnly", "true");
      
      // Add includeTeamNames parameter if requested
      if (options.includeTeamNames) params.append("includeTeamNames", "true");

      const queryString = params.toString();
      const url = queryString ? `/transfers/market?${queryString}` : "/transfers/market";

      // Add signal to request config
      const config = {};
      if (options.signal) {
        config.signal = options.signal;
      }

      return await api.get(url, config);
    } catch (error) {
      // Don't retry if request was cancelled
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        throw error;
      }

      console.error("Error fetching market players:", error);

      // Retry logic for network errors
      const retries = options.retries || 2;
      if (retries > 0 && (error.code === "ERR_NETWORK" || !error.response)) {
        console.log(
          `Network error in getMarketPlayers, retrying... (${retries} attempts left)`
        );
        const delay = Math.min(1000 * Math.pow(2, 2 - retries), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return TransferService.getMarketPlayers(filters, { ...options, retries: retries - 1 });
      }

      throw error;
    }
  },

  /**
   * Get filter metadata for transfer market
   * @returns {Promise<Object>} Filter options and ranges
   */
  async getMarketFilters(params = {}) {
    try {
      const config = {};
      if (params && params.leagueId) {
        config.params = { leagueId: params.leagueId };
      }
      return await api.get("/transfers/market/filters", config);
    } catch (error) {
      console.error("Error fetching market filters:", error);
      throw error;
    }
  },

  /**
   * Get active transfer bids for a team
   * @param {string} teamId - Team ID
   * @returns {Promise<Array>} - List of active bids
   */
  async getTeamBids(teamId, leagueId) {
    if (!leagueId) throw new Error('leagueId is required for getTeamBids');
    return api.get(`/transfers/bids/team/${teamId}`, { params: { leagueId } });
  },

  /**
   * Get active transfer bids for a team
   * @param {string} leagueId - League ID
   * @returns {Promise<Array>} - List of active bids
   */
  async getBids(leagueId) {
    if (!leagueId) {
      throw new Error('leagueId is required but was not supplied to TransferService.getBids');
    }
    const config = { params: { leagueId } };
    return api.get(`/transfers/bids`, config);
  },

  /**
   * Get transfer history
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.teamId - Filter by team ID
   * @param {string} params.leagueId - League ID
   * @param {string} params.season - Filter by season
   * @returns {Promise<Object>} - Transfer history with pagination info
   */
  async getTransferHistory(params = {}) {
    if (!params.leagueId) {
      throw new Error('leagueId is required but was not supplied to TransferService.getTransferHistory');
    }

    const queryParams = new URLSearchParams();

    queryParams.append("leagueId", params.leagueId);
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.teamId) queryParams.append("teamId", params.teamId);
    if (params.season) queryParams.append("season", params.season);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/transfers/history?${queryString}`
      : "/transfers/history";

    return api.get(url);
  },

  /**
   * Submit a transfer bid
   * @param {Object} bidData - Bid information
   * @param {string} bidData.playerId - Player ID
   * @param {number} bidData.amount - Bid amount
   * @param {Object} bidData.contractOffer - Contract offer details using length and salary
   * @param {string} leagueId - League ID
   * @returns {Promise<Object>} - Created bid
   */
  async submitBid(bidData, leagueId) {
    if (!leagueId) {
      throw new Error('leagueId is required but was not supplied to TransferService.submitBid');
    }
    const data = { ...bidData, leagueId };
    return api.post("/transfers/bid", data);
  },

  /**
   * Get current transfer window information
   * @returns {Promise<Object>} - Transfer window details
   */
  async getTransferWindowInfo(leagueId = null) {
    const config = leagueId ? { params: { leagueId } } : {};
    return api.get("/transfers/window", config);
  },

  /**
   * Get team information for transfers with retry logic
   * @param {string} leagueId - League ID
   * @param {number} retries - Number of retries for network errors
   * @returns {Promise<Object>} - Team transfer information including budget and squad size
   */
  async getTeamInfo(leagueId, retries = 2) {
    try {
      const config = leagueId ? { params: { leagueId } } : {};
      const response = await api.get("/transfers/team-info", config);
      // Format the response to match what the frontend expects
      return {
        data: {
          boosted: false, // Default value since it's not provided by the backend
          transferBudget: response.transferBudget || response.budget, // Use new transferBudget field
          remainingBudget: response.remainingBudget || response.budget, // Use new remainingBudget field
          startingBudget: response.startingBudget, // Budget every team starts with; undefined on older backends
          pendingBidTotal: response.pendingBidTotal || 0, // Amount tied up in pending bids
          squadSize: response.squadSize,
          maxSquadSize: response.maxSquadSize,
          transferWindow: response.transferWindow,
          teamId: response.teamId,
          name: response.name,
          players: response.players, // Ensure players array is passed through
        },
      };
    } catch (error) {
      console.error("Error fetching team info:", error);

      // Retry logic for network errors
      if (retries > 0 && (error.code === "ERR_NETWORK" || !error.response)) {
        console.log(
          `Network error in getTeamInfo, retrying... (${retries} attempts left)`
        );
        // Wait before retrying with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, 2 - retries), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return TransferService.getTeamInfo(leagueId, retries - 1);
      }

      throw error;
    }
  },

  /**
   * Boost the team's transfer budget
   * @param {Object} boostData - Boost option details
   * @param {string} leagueId - League ID
   * @returns {Promise<Object>} - Updated team budget information
   */
  async boostTransferBudget(boostData, leagueId) {
    const data = leagueId ? { ...boostData, leagueId } : boostData;
    return api.post("/transfers/boost-budget", data);
  },

  /**
   * Get latest market data for a single player (including auction info)
   * @param {number|string} wyId - Player WyScout ID
   * @param {string} leagueId - League ID used to resolve ownership
   * @returns {Promise<Object>} - Player market data
   */
  async getPlayerMarketData(wyId, leagueId) {
    if (!wyId) throw new Error('wyId is required');
    if (!leagueId) throw new Error('leagueId is required');
    return api.get(`/transfers/market/player/${wyId}`, { params: { leagueId } });
  },

  // Admin Transfer Window Management
  async createOrUpdateTransferWindow(windowData) {
    return api.post("/transfers/window", windowData);
  },

  async closeTransferWindow(closeData) {
    return api.post("/transfers/window/close", closeData);
  },

  async getCurrentTransferWindowInfo(leagueId = null) {
    const config = leagueId ? { params: { leagueId } } : {};
    return api.get("/transfers/window", config);
  },
};

export default TransferService;
